import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import React, {useMemo} from "react";
import {
    createDayOfMonthLabels,
    extractAvailableSeasons,
    extractSeason, getDayOfYearLabel,
    getSeason,
    groupBy,
    isLeapYear,
    trimLabels
} from "../utils";
import Toolbar, {useToolbar} from "./Toolbar";
import HighchartsReact from "highcharts-react-official";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import Stack from "@mui/material/Stack";
import {blue, red} from "@mui/material/colors";


const ObservedAvalanches = ({data: originalData, microZoneName= ""}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const data = useMemo(() => (originalData.map(({date, ...rest}) => {
            return {
                date,
                label: getDayOfYearLabel(date),
                season: getSeason(date),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbarState = useToolbar(availableSeasons);

    const selectedSeasonData = groupBy(extractSeason(data, toolbarState.selectedSeason), 'label')
    const withLeapYears = availableSeasons.some(y => isLeapYear(y) + 1)
    const labels = createDayOfMonthLabels(withLeapYears);
    const trimmedLabels = trimLabels(labels, selectedSeasonData);

    const selectedSeasonLabel = toolbarState.selectedSeason ? (toolbarState.selectedSeason + '/' + (toolbarState.selectedSeason + 1)) : '';

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `${microZoneName ?? 'n'}_observed_avalanches`
        },
        title: {
            text: t('observedAvalanches.title') + ' ' + microZoneName ?? ''
        },
        subtitle: {
            text: t('subtitle.season', {season: selectedSeasonLabel})
        },
        series: [
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.models1ReportingNoAvalancheCount ?? null),
                type: 'area',
                step: 'left',
                name: t('observedAvalanches.legend.models1ReportingNoAvalanches'),
                color: blue[500],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.models1ReportingAvalancheCount ?? null),
                type: 'area',
                step: 'left',
                name: t('observedAvalanches.legend.models1ReportingAvalanches'),
                color: red[300],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
        ],
        xAxis: {
            categories: trimmedLabels,
            gridLineWidth: 1,
            crosshair: true
        },
        yAxis: [
            { // First y-axis
                title: {
                    text: t('observedAvalanches.numberOfModels1'),
                    gridLineWidth: 1
                }
            },
        ],
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                area: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions,
        },
        ...creditsOptions,
        tooltip: {
            shared: true
        }
    }

    return <Stack>
        <Toolbar {...toolbarState} availableSeasons={availableSeasons} />
        <HighchartsReact highcharts={Highcharts} options={options} />
    </Stack>
}


export default ObservedAvalanches;